import { logError } from '@/utils/logger';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Pencil, Trash, ShieldCheck, MapPin, Plus, Key, Database, DownloadSimple, UploadSimple, ClockCounterClockwise, ArrowsClockwise, FloppyDisk, Crosshair, Warning, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import PhotoUpload from '@/components/PhotoUpload';
import UserPermissionsDialog from '@/components/UserPermissionsDialog';
import UserScopeDialog from '@/components/UserScopeDialog';

const ROLES = [
  { value: 'admin', label: 'Administrateur', description: 'Accès complet à toutes les zones' },
  { value: 'finance', label: 'Finance', description: 'Peut voir tout, modifier uniquement finance' },
  { value: 'drh', label: 'DRH', description: 'Agents, affectations, logistique (sa zone)' },
  { value: 'supervisor', label: 'Superviseur', description: 'Clients, agents, affectations (sa zone)' },
  { value: 'inspector', label: 'Inspecteur', description: 'Sites et rapports uniquement (sa zone)' },
  { value: 'logistics', label: 'Logistique', description: 'Stock uniquement (sa zone)' },
  { value: 'viewer', label: 'Viewer', description: 'Lecture seule du dashboard' },
];

const ROLE_COLORS = {
  admin: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30',
  finance: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30',
  drh: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30',
  supervisor: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30',
  inspector: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30',
  logistics: 'bg-[#EC4899]/20 text-[#EC4899] border-[#EC4899]/30',
  viewer: 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30',
};

const Admin = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isResetPwOpen, setIsResetPwOpen] = useState(false);
  const [isZoneDialogOpen, setIsZoneDialogOpen] = useState(false);
  const [isPermsOpen, setIsPermsOpen] = useState(false);
  const [permsTargetUser, setPermsTargetUser] = useState(null);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const [scopeTargetUser, setScopeTargetUser] = useState(null);

  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ role: '', zone_id: '', name: '' });
  const [createFormData, setCreateFormData] = useState({ name: '', email: '', password: '', role: 'viewer', zone_id: '', picture: '', global_access: false });
  const [newPassword, setNewPassword] = useState('');
  const [zoneFormData, setZoneFormData] = useState({ name: '', code: '' });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [backups, setBackups] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);

  // Reset / Format Data
  const [resetPreview, setResetPreview] = useState({});
  const [resetSelected, setResetSelected] = useState({});
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetHard, setResetHard] = useState(false);
  const [resetCreateBackup, setResetCreateBackup] = useState(true);
  const [resetBusy, setResetBusy] = useState(false);

  const RESET_MODULES = [
    { key: 'clients', label: 'Clients & sites', desc: 'Clients et localisations GPS' },
    { key: 'agents', label: 'Agents & affectations', desc: 'Agents, dossiers RH, affectations' },
    { key: 'reports', label: 'Rapports d\'inspection', desc: 'Rapports + rapports IA' },
    { key: 'logistics', label: 'Logistique & stock', desc: 'Uniformes, équipements, entrées/sorties' },
    { key: 'finance', label: 'Finances', desc: 'Frais + charges d\'exploitation' },
    { key: 'supervisors', label: 'Superviseurs', desc: 'Site supervisors' },
    { key: 'messages', label: 'Messagerie interne', desc: 'Messages + conversations + annonces' },
    { key: 'notifications', label: 'Notifications', desc: 'Alertes + push subscriptions' },
    { key: 'audit', label: 'Journal d\'audit', desc: 'Audit logs + tentatives de login' },
    { key: 'trash', label: 'Corbeille', desc: 'Vide la corbeille définitivement' },
    { key: 'files', label: 'Fichiers uploadés', desc: 'Métadonnées fichiers (photos, PDF)' },
    { key: 'projects', label: 'Documents projets', desc: 'Documents attachés aux projets' },
    { key: 'ai', label: 'Historique IA', desc: 'Conversations avec l\'assistant Claude' },
  ];

  const fetchResetPreview = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-data/preview`, { credentials: 'include' });
      if (res.ok) setResetPreview(await res.json());
    } catch (e) { logError('reset preview', e); }
  };

  const openResetDialog = () => {
    setResetSelected({});
    setResetConfirmation('');
    setResetPassword('');
    setResetHard(false);
    setResetCreateBackup(true);
    fetchResetPreview();
    setResetOpen(true);
  };

  const toggleResetModule = (key) => {
    setResetSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllReset = (mode) => {
    if (mode === 'none') { setResetSelected({}); return; }
    const all = {};
    RESET_MODULES.forEach(m => {
      if (mode === 'operational' && ['audit', 'trash', 'files'].includes(m.key)) return;
      all[m.key] = true;
    });
    setResetSelected(all);
  };

  const handleResetSubmit = async () => {
    const modules = Object.keys(resetSelected).filter(k => resetSelected[k]);
    if (modules.length === 0) { toast.error('Sélectionnez au moins un module'); return; }
    if (resetConfirmation !== 'SUPPRIMER') { toast.error('Tapez SUPPRIMER pour confirmer'); return; }
    if (!resetPassword) { toast.error('Entrez votre mot de passe admin'); return; }
    setResetBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-data`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modules,
          confirmation: resetConfirmation,
          password: resetPassword,
          create_backup: resetCreateBackup,
          hard_delete: resetHard,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Erreur');
        setResetBusy(false);
        return;
      }
      toast.success(`${data.total_deleted} enregistrement(s) supprimé(s)${data.hard_delete ? ' définitivement' : ' (corbeille)'}`);
      setResetOpen(false);
      fetchResetPreview();
      fetchBackups();
    } catch (e) {
      logError('reset submit', e);
      toast.error('Erreur');
    }
    setResetBusy(false);
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit-logs?limit=300`, { credentials: 'include' });
      if (res.ok) setAuditLogs(await res.json());
    } catch (error) { logError('Fetch audit logs:', error); }
  };

  const fetchBackups = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups`, { credentials: 'include' });
      if (res.ok) setBackups(await res.json());
    } catch (error) { logError('Fetch backups:', error); }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Sauvegarde créée (${data.total_records} enregistrements)`);
      fetchBackups();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setCreatingBackup(false); }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${backupId}/download`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : 'gazelle-backup.json';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Erreur lors du téléchargement'); }
  };

  const fmtLogDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-data`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : 'gazelle-export.json';
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Export téléchargé');
    } catch { toast.error("Erreur lors de l'export"); }
    finally { setExporting(false); }
  };

  const handleImportData = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`⚠️ ATTENTION : l'importation va REMPLACER toutes les données actuelles de cette application par celles du fichier "${file.name}".\n\nContinuer ?`)) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-data`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setImportResult(data);
      toast.success(`Importation réussie : ${data.total} enregistrements. Reconnectez-vous si nécessaire.`);
    } catch (err) { toast.error(err.message || "Erreur lors de l'import"); }
    finally { setImporting(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (zonesRes.ok) {
        const z = await zonesRes.json();
        setZones(z);
        if (z.length > 0 && !createFormData.zone_id) {
          setCreateFormData(prev => ({ ...prev, zone_id: z[0].zone_id }));
        }
      }
    } catch (error) {
      logError('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // ---- CREATE USER ----
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createFormData),
      });
      if (response.ok) {
        toast.success('Utilisateur créé');
        setIsCreateUserOpen(false);
        setCreateFormData({ name: '', email: '', password: '', role: 'viewer', zone_id: zones[0]?.zone_id || '', picture: '' });
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur lors de la création'); }
  };

  // ---- EDIT USER ----
  const openEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({ role: user.role, zone_id: user.zone_id || '', name: user.name || '', picture: user.picture || '', global_access: !!user.global_access, signature_url: user.signature_url || '' });
    setIsEditUserOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.user_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData),
      });
      if (response.ok) {
        toast.success('Utilisateur modifié');
        setIsEditUserOpen(false);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur lors de la modification'); }
  };

  // ---- RESET PASSWORD ----
  const openResetPw = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setIsResetPwOpen(true);
  };

  // ---- PERMISSIONS ----
  const openPermissions = (user) => {
    setPermsTargetUser(user);
    setIsPermsOpen(true);
  };

  const openScope = (user) => {
    setScopeTargetUser(user);
    setIsScopeOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.user_id}/password`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });
      if (response.ok) {
        toast.success('Mot de passe réinitialisé');
        setIsResetPwOpen(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur'); }
  };

  // ---- DELETE USER ----
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer l'utilisateur "${userName}" ? Cette action est irréversible.`)) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (response.ok) {
        toast.success('Utilisateur supprimé');
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  // ---- CREATE ZONE ----
  const handleCreateZone = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(zoneFormData),
      });
      if (response.ok) {
        toast.success('Zone créée');
        setIsZoneDialogOpen(false);
        setZoneFormData({ name: '', code: '' });
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur lors de la création'); }
  };

  const getZoneName = (zoneId) => zones.find(z => z.zone_id === zoneId)?.name || '-';
  const getRoleBadge = (role) => {
    const roleInfo = ROLES.find(r => r.value === role);
    return <Badge className={`${ROLE_COLORS[role] || ROLE_COLORS.viewer} border`}>{roleInfo?.label || role}</Badge>;
  };

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-4xl font-bold text-[#F8FAFC] tracking-tight">Administration</h1>
        <p className="text-[#94A3B8] mt-2">Gestion des utilisateurs et des zones</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#1E293B] border border-[#334155]">
          <TabsTrigger value="users" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
            <Users size={18} className="mr-2" /> Utilisateurs ({users.length})
          </TabsTrigger>
          <TabsTrigger value="zones" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
            <MapPin size={18} className="mr-2" /> Zones
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
            <ShieldCheck size={18} className="mr-2" /> Rôles
          </TabsTrigger>
          <TabsTrigger value="data" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white" data-testid="data-tab" onClick={fetchBackups}>
            <Database size={18} className="mr-2" /> Données
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white" data-testid="audit-tab" onClick={fetchAuditLogs}>
            <ClockCounterClockwise size={18} className="mr-2" /> Historique
          </TabsTrigger>
        </TabsList>

        {/* ========== USERS TAB ========== */}
        <TabsContent value="users" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-user-button">
                  <Plus size={20} className="mr-2" /> Ajouter un utilisateur
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Nouvel utilisateur</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div className="flex justify-center">
                    <PhotoUpload currentUrl={createFormData.picture} onUploaded={(url) => setCreateFormData({ ...createFormData, picture: url })} size="lg" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Nom complet</Label>
                    <Input value={createFormData.name} onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })} required placeholder="Nom complet" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="create-user-name" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Email</Label>
                    <Input type="email" value={createFormData.email} onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })} required placeholder="email@exemple.com" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="create-user-email" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Mot de passe</Label>
                    <Input type="password" value={createFormData.password} onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })} required minLength={6} placeholder="Minimum 6 caractères" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="create-user-password" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#F8FAFC]">Rôle</Label>
                      <Select value={createFormData.role} onValueChange={(v) => setCreateFormData({ ...createFormData, role: v })}>
                        <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                          {ROLES.map(r => <SelectItem key={r.value} value={r.value} className="text-[#F8FAFC]">{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#F8FAFC]">Zone</Label>
                      <Select value={createFormData.zone_id} onValueChange={(v) => setCreateFormData({ ...createFormData, zone_id: v })}>
                        <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                          {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button type="button" onClick={() => setCreateFormData({ ...createFormData, global_access: !createFormData.global_access })} className={`w-full h-10 rounded-md border text-sm font-medium transition-colors ${createFormData.global_access ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-[#1E293B] border-[#334155] text-[#94A3B8] hover:border-[#8B5CF6]'}`} data-testid="create-global-access-toggle">
                    {createFormData.global_access ? '✓ Directeur Général — accès à TOUTES les zones' : 'Directeur Général (accès toutes zones) : désactivé'}
                  </button>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsCreateUserOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                    <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-new-user">Créer</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1E293B]/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Rôle</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Zone</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {users.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-[#94A3B8]">Aucun utilisateur</td></tr>
                  ) : users.map((user) => (
                    <tr key={user.user_id} className="hover:bg-[#1E293B]/30 transition-colors" data-testid={`user-row-${user.user_id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium">
                              {user.name?.charAt(0) || 'U'}
                            </div>
                          )}
                          <span className="text-[#F8FAFC] font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#94A3B8]">{user.email}</td>
                      <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4">
                        <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">{getZoneName(user.zone_id)}</Badge>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openPermissions(user)} className="text-[#10B981] hover:text-white hover:bg-[#10B981]" title="Permissions" data-testid={`permissions-user-${user.user_id}`}>
                            <ShieldCheck size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openScope(user)} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]" title="Périmètre d'accès" data-testid={`scope-user-${user.user_id}`}>
                            <Crosshair size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditUser(user)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]" title="Modifier">
                            <Pencil size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openResetPw(user)} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]" title="Réinitialiser mot de passe">
                            <Key size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user.user_id, user.name)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]" title="Supprimer">
                            <Trash size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Edit User Dialog */}
          <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
            <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-2xl font-bold">Modifier l'utilisateur</DialogTitle></DialogHeader>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="flex justify-center items-end gap-8">
                  <div className="text-center">
                    <PhotoUpload currentUrl={editFormData.picture} onUploaded={(url) => setEditFormData({ ...editFormData, picture: url })} size="lg" />
                    <p className="text-[10px] text-[#64748B] mt-1">Photo</p>
                  </div>
                  <div className="text-center">
                    {editFormData.signature_url && <img src={editFormData.signature_url} alt="Signature" className="max-h-14 bg-white rounded p-1 mb-1 mx-auto" data-testid="user-signature-preview" />}
                    <PhotoUpload currentUrl={editFormData.signature_url} onUploaded={(url) => setEditFormData({ ...editFormData, signature_url: url })} size="sm" />
                    <p className="text-[10px] text-[#64748B] mt-1">Signature (rapports)</p>
                  </div>
                </div>
                <div>
                  <Label className="text-[#94A3B8]">Utilisateur</Label>
                  <p className="text-[#F8FAFC] font-medium">{editingUser?.name}</p>
                  <p className="text-sm text-[#94A3B8]">{editingUser?.email}</p>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Nom</Label>
                  <Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Rôle</Label>
                  <Select value={editFormData.role} onValueChange={(v) => setEditFormData({ ...editFormData, role: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      {ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value} className="text-[#F8FAFC]">
                          <div><div className="font-medium">{r.label}</div><div className="text-xs text-[#94A3B8]">{r.description}</div></div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Zone</Label>
                  <Select value={editFormData.zone_id} onValueChange={(v) => setEditFormData({ ...editFormData, zone_id: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => setEditFormData({ ...editFormData, global_access: !editFormData.global_access })} className={`w-full h-10 rounded-md border text-sm font-medium transition-colors ${editFormData.global_access ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-[#1E293B] border-[#334155] text-[#94A3B8] hover:border-[#8B5CF6]'}`} data-testid="edit-global-access-toggle">
                  {editFormData.global_access ? '✓ Directeur Général — accès à TOUTES les zones' : 'Directeur Général (accès toutes zones) : désactivé'}
                </button>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsEditUserOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                  <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Enregistrer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Reset Password Dialog */}
          <Dialog open={isResetPwOpen} onOpenChange={setIsResetPwOpen}>
            <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-2xl font-bold">Réinitialiser le mot de passe</DialogTitle></DialogHeader>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-[#94A3B8]">Utilisateur</Label>
                  <p className="text-[#F8FAFC] font-medium">{editingUser?.name} ({editingUser?.email})</p>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">Nouveau mot de passe</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} placeholder="Minimum 6 caractères" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="reset-password-input" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsResetPwOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                  <Button type="submit" className="bg-[#F59E0B] hover:bg-[#D97706] text-white">Réinitialiser</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========== ZONES TAB ========== */}
        <TabsContent value="zones" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={isZoneDialogOpen} onOpenChange={setIsZoneDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"><Plus size={20} className="mr-2" /> Nouvelle zone</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="text-2xl font-bold">Créer une zone</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateZone} className="space-y-4">
                  <div>
                    <Label className="text-[#F8FAFC]">Nom de la zone</Label>
                    <Input value={zoneFormData.name} onChange={(e) => setZoneFormData({ ...zoneFormData, name: e.target.value })} required placeholder="Ex: Goma" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Code (3 lettres)</Label>
                    <Input value={zoneFormData.code} onChange={(e) => setZoneFormData({ ...zoneFormData, code: e.target.value.toUpperCase() })} maxLength={3} placeholder="Ex: GOM" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsZoneDialogOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                    <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Créer</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zones.map(zone => (
              <div key={zone.zone_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#F8FAFC]">{zone.name}</h3>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">{zone.code}</Badge>
                </div>
                <p className="text-sm text-[#94A3B8]">{users.filter(u => u.zone_id === zone.zone_id).length} utilisateurs</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ========== ROLES TAB ========== */}
        <TabsContent value="roles" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ROLES.map(role => (
              <div key={role.value} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
                <div className="flex items-center gap-3 mb-3">
                  {getRoleBadge(role.value)}
                  <span className="text-[#94A3B8] text-sm">({users.filter(u => u.role === role.value).length} utilisateurs)</span>
                </div>
                <p className="text-[#F8FAFC]">{role.description}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ========== DATA TAB ========== */}
        <TabsContent value="data" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <DownloadSimple size={20} className="text-[#10B981]" weight="duotone" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">Exporter les données</h3>
                  <p className="text-xs text-[#94A3B8]">Télécharge toutes les données en un fichier JSON</p>
                </div>
              </div>
              <p className="text-sm text-[#94A3B8]">Toutes les collections (agents, clients, affectations, finances, utilisateurs, etc.) sont exportées dans un seul fichier de sauvegarde.</p>
              <Button onClick={handleExportData} disabled={exporting} className="bg-[#10B981] hover:bg-[#059669] text-white w-full" data-testid="export-data-button">
                <DownloadSimple size={18} className="mr-2" /> {exporting ? 'Export en cours...' : 'Exporter tout (JSON)'}
              </Button>
            </div>

            <div className="bg-[#0F172A] border border-[#F59E0B]/30 rounded-md p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <UploadSimple size={20} className="text-[#F59E0B]" weight="duotone" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">Importer les données</h3>
                  <p className="text-xs text-[#94A3B8]">Restaure un fichier d'export JSON</p>
                </div>
              </div>
              <p className="text-sm text-[#F59E0B]">⚠️ Remplace TOUTES les données actuelles par celles du fichier. Utilisez un fichier exporté depuis l'application. Reconnectez-vous après l'import.</p>
              <label className="block">
                <input type="file" accept=".json,application/json" onChange={handleImportData} disabled={importing} className="hidden" id="import-file-input" data-testid="import-file-input" />
                <Button asChild disabled={importing} className="bg-[#F59E0B] hover:bg-[#D97706] text-white w-full cursor-pointer" data-testid="import-data-button">
                  <span onClick={() => document.getElementById('import-file-input').click()}>
                    <UploadSimple size={18} className="mr-2" /> {importing ? 'Importation en cours...' : 'Importer un fichier (JSON)'}
                  </span>
                </Button>
              </label>
              {importResult && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-md p-3 text-sm" data-testid="import-result">
                  <p className="text-[#10B981] font-medium mb-1">✓ {importResult.total} enregistrements importés</p>
                  <div className="text-xs text-[#94A3B8] grid grid-cols-2 gap-x-3">
                    {Object.entries(importResult.collections).map(([col, n]) => (
                      <span key={col}>{col} : {n}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Automatic backups */}
          <div className="mt-4 bg-[#0F172A] border border-[#1E293B] rounded-md p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
                  <FloppyDisk size={20} className="text-[#2563EB]" weight="duotone" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">Sauvegardes automatiques</h3>
                  <p className="text-xs text-[#94A3B8]">Une sauvegarde complète est créée chaque semaine (8 dernières conservées)</p>
                </div>
              </div>
              <Button onClick={handleCreateBackup} disabled={creatingBackup} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="create-backup-button">
                <FloppyDisk size={16} className="mr-2" /> {creatingBackup ? 'En cours...' : 'Sauvegarder maintenant'}
              </Button>
            </div>
            {backups.length === 0 ? (
              <p className="text-sm text-[#64748B] text-center py-4">Aucune sauvegarde pour le moment — la première sera créée automatiquement</p>
            ) : (
              <div className="divide-y divide-[#1E293B]">
                {backups.map(b => (
                  <div key={b.backup_id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`backup-${b.backup_id}`}>
                    <div>
                      <p className="text-sm text-[#F8FAFC] font-medium">{fmtLogDate(b.created_at)}</p>
                      <p className="text-xs text-[#94A3B8]">{b.total_records} enregistrements • {(b.size_bytes / 1024).toFixed(0)} Ko • {b.trigger === 'auto' ? 'Automatique' : 'Manuelle'}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadBackup(b.backup_id)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] self-end sm:self-auto" data-testid={`download-backup-${b.backup_id}`}>
                      <DownloadSimple size={15} className="mr-1.5" /> Télécharger
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ========== Réinitialisation des données ========== */}
          <div className="mt-4 bg-[#0F172A] border border-[#EF4444]/40 rounded-md p-6 space-y-4" data-testid="reset-data-panel">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#EF4444]/20 flex items-center justify-center">
                  <Warning size={20} className="text-[#EF4444]" weight="duotone" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC]">Zone de danger — Réinitialiser les données</h3>
                  <p className="text-xs text-[#94A3B8]">Formater plusieurs modules d&apos;un coup pour repartir de zéro</p>
                </div>
              </div>
              <Button
                type="button"
                onClick={openResetDialog}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                data-testid="open-reset-data-btn"
              >
                <Warning size={16} className="mr-2" weight="bold" />
                Réinitialiser des modules
              </Button>
            </div>
            <p className="text-xs text-[#F59E0B]">
              ⚠️ Cette action supprime plusieurs collections en un seul clic. Par défaut, les données vont dans la Corbeille (récupérables 30 jours).
              Une sauvegarde JSON est créée automatiquement avant chaque réinitialisation.
            </p>
          </div>
        </TabsContent>

        {/* ========== AUDIT TAB ========== */}
        <TabsContent value="audit" className="mt-6">
          <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-[#1E293B] flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#F8FAFC]">Historique des actions</h3>
                <p className="text-xs text-[#94A3B8]">{auditLogs.length} action(s) récentes — qui a fait quoi et quand</p>
              </div>
              <Button size="sm" variant="ghost" onClick={fetchAuditLogs} className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B]" data-testid="refresh-audit-button">
                <ArrowsClockwise size={16} />
              </Button>
            </div>
            {auditLogs.length === 0 ? (
              <div className="px-6 py-10 text-center text-[#94A3B8]">
                <ClockCounterClockwise size={40} className="mx-auto mb-3 opacity-40" />
                <p>Aucune action enregistrée pour le moment</p>
                <p className="text-xs text-[#64748B] mt-1">Les créations, modifications et suppressions apparaîtront ici</p>
              </div>
            ) : (
              <>
                <div className="px-4 sm:px-6 py-3 border-b border-[#1E293B]">
                  <Input value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} placeholder="Rechercher par utilisateur, action ou email..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="search-audit" />
                </div>
                <div className="max-h-[600px] overflow-y-auto divide-y divide-[#1E293B]">
                {auditLogs.filter(l => {
                  if (!auditSearch) return true;
                  const q = auditSearch.toLowerCase();
                  return l.user_name?.toLowerCase().includes(q) || l.user_email?.toLowerCase().includes(q) || l.action?.toLowerCase().includes(q);
                }).map(log => (
                  <div key={log.log_id} className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 hover:bg-[#1E293B]/30 transition-colors" data-testid={`audit-log-${log.log_id}`}>
                    <span className="text-xs text-[#64748B] font-mono sm:w-36 flex-shrink-0">{fmtLogDate(log.timestamp)}</span>
                    <span className="text-sm text-[#F8FAFC] font-medium sm:w-48 truncate flex-shrink-0">{log.user_name || log.user_email}</span>
                    <span className="text-sm text-[#94A3B8] flex-1 truncate">{log.action}</span>
                    <Badge className={`self-start sm:self-auto text-xs border ${log.success ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' : 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30'}`}>
                      {log.success ? 'Réussi' : `Échec (${log.status_code})`}
                    </Badge>
                  </div>
                ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <UserPermissionsDialog open={isPermsOpen} onOpenChange={setIsPermsOpen} user={permsTargetUser} />
      <UserScopeDialog open={isScopeOpen} onOpenChange={setIsScopeOpen} user={scopeTargetUser} />

      {/* Reset Data Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0F172A] border-[#EF4444]/50 text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Warning size={22} className="text-[#EF4444]" weight="fill" />
              Réinitialiser les données
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3 text-xs text-[#FCA5A5]">
              Cette opération supprime les données des modules cochés. Par défaut elles vont dans la <b>Corbeille</b> (récupérables 30 j).
              Cochez « Purge définitive » pour supprimer sans retour possible.
              Les utilisateurs, zones et sauvegardes sont <b>protégés</b>.
            </div>

            {/* Presets */}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => selectAllReset('operational')} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] text-xs" data-testid="reset-preset-operational">
                Tout opérationnel
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => selectAllReset('all')} className="border-[#EF4444]/50 text-[#FCA5A5] hover:bg-[#EF4444]/10 text-xs" data-testid="reset-preset-all">
                Tout sauf utilisateurs
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => selectAllReset('none')} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] text-xs" data-testid="reset-preset-none">
                Tout décocher
              </Button>
            </div>

            {/* Modules list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {RESET_MODULES.map(m => {
                const count = resetPreview?.[m.key]?.total ?? 0;
                const checked = !!resetSelected[m.key];
                return (
                  <label
                    key={m.key}
                    className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                      checked
                        ? 'bg-[#EF4444]/10 border-[#EF4444]/50'
                        : 'bg-[#1E293B]/40 border-[#334155] hover:bg-[#1E293B]/70'
                    }`}
                    data-testid={`reset-module-${m.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleResetModule(m.key)}
                      className="mt-0.5 accent-[#EF4444]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-[#F8FAFC] font-medium">{m.label}</p>
                        <Badge className="bg-[#334155]/50 text-[#F8FAFC] border border-[#334155] text-xs">
                          {count}
                        </Badge>
                      </div>
                      <p className="text-[10px] text-[#94A3B8] mt-0.5 truncate">{m.desc}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Options */}
            <div className="space-y-2 pt-2 border-t border-[#1E293B]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetCreateBackup}
                  onChange={(e) => setResetCreateBackup(e.target.checked)}
                  className="accent-[#10B981]"
                  data-testid="reset-backup-checkbox"
                />
                <span className="text-sm text-[#F8FAFC]">Créer une sauvegarde JSON avant suppression <span className="text-[#94A3B8] text-xs">(recommandé)</span></span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetHard}
                  onChange={(e) => setResetHard(e.target.checked)}
                  className="accent-[#EF4444]"
                  data-testid="reset-hard-checkbox"
                />
                <span className="text-sm text-[#FCA5A5]">Purge définitive <span className="text-[#94A3B8] text-xs">(pas de corbeille, irréversible)</span></span>
              </label>
            </div>

            {/* Confirmations */}
            <div className="space-y-3 pt-2 border-t border-[#1E293B]">
              <div>
                <Label className="text-[#F8FAFC]">Tapez <span className="text-[#EF4444] font-bold">SUPPRIMER</span> pour confirmer</Label>
                <Input
                  type="text"
                  value={resetConfirmation}
                  onChange={(e) => setResetConfirmation(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] mt-1"
                  data-testid="reset-confirmation-input"
                />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Votre mot de passe admin</Label>
                <Input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] mt-1"
                  data-testid="reset-password-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setResetOpen(false)}
                className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleResetSubmit}
                disabled={resetBusy || resetConfirmation !== 'SUPPRIMER' || !resetPassword || Object.values(resetSelected).filter(Boolean).length === 0}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                data-testid="reset-submit-btn"
              >
                <Warning size={16} className="mr-2" weight="bold" />
                {resetBusy ? 'Suppression...' : (resetHard ? 'Purge définitive' : 'Envoyer dans la corbeille')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
